if (!customElements.get("cart-shipping-upsell-panel")) {
  class CartShippingUpsellPanel extends HTMLElement {
    constructor() {
      super();
      this.checkoutBypass = false;
      this.queuedTrigger = null;
      this.onCheckoutClick = this.onCheckoutClick.bind(this);
      this.onPanelClose = this.onPanelClose.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.onCookieConsentChange = this.onCookieConsentChange.bind(this);
    }

    connectedCallback() {
      this.contentRoot = this.querySelector("#cart-shipping-upsell-panel");
      this.bindPanelEvents();
      this.syncFromContent();
      this.watchCookieConsent();

      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        this.cartUpdateUnsubscriber = subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          this.onCartUpdate
        );
      }

      document.addEventListener("click", this.onCheckoutClick, true);
    }

    disconnectedCallback() {
      this.cartUpdateUnsubscriber?.();
      this.cookieObserver?.disconnect();
      document.removeEventListener("click", this.onCheckoutClick, true);
    }

    isCookieBannerVisible() {
      return ["shopify-pc__banner", "shopify-pc__prefs__modal"].some((id) => {
        const element = document.getElementById(id);
        if (!element) return false;

        const style = window.getComputedStyle(element);
        if (style.display === "none" || style.visibility === "hidden") {
          return false;
        }

        if (parseFloat(style.opacity) === 0) return false;

        const rect = element.getBoundingClientRect();
        return rect.width > 0 || rect.height > 0;
      });
    }

    isCartDrawerOpen() {
      return document
        .getElementById("offcanvas__mini_cart")
        ?.classList.contains("active");
    }

    isMobileViewport() {
      return window.matchMedia("(max-width: 749px)").matches;
    }

    shouldUsePanel() {
      return this.isUnderThreshold() && !this.isMobileViewport();
    }

    watchCookieConsent() {
      this.cookieObserver = new MutationObserver(this.onCookieConsentChange);
      this.cookieObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class", "hidden", "aria-hidden"],
      });
    }

    onCookieConsentChange() {
      if (this.isCookieBannerVisible()) {
        if (this.classList.contains("active")) {
          this.queuedTrigger = this.queuedTrigger || "drawer-open";
          this.hide({ preserveQueue: true });
        }
        return;
      }

      if (this.queuedTrigger && this.isCartDrawerOpen()) {
        this.show(this.queuedTrigger);
      }
    }

    bindPanelEvents() {
      this.querySelector("[data-upsell-panel-close]")?.addEventListener(
        "click",
        this.onPanelClose
      );
    }

    syncFromContent() {
      const content = this.querySelector("[data-upsell-panel-content]");
      if (!content) {
        this.hide();
        return;
      }

      this.thresholdCents = parseInt(content.dataset.thresholdCents, 10) || 0;
      this.cartTotalCents = parseInt(content.dataset.cartTotalCents, 10) || 0;
      this.remainingCents = parseInt(content.dataset.remainingCents, 10) || 0;
      this.underThreshold = content.dataset.underThreshold === "true";

      if (!this.underThreshold) {
        this.hide();
      }
    }

    isEnabled() {
      return Boolean(this.querySelector("[data-upsell-panel-content]"));
    }

    isUnderThreshold() {
      return this.isEnabled() && this.underThreshold;
    }

    show(trigger = "drawer-open") {
      if (!this.shouldUsePanel()) return;

      if (this.isCookieBannerVisible()) {
        this.queuedTrigger = trigger;
        return;
      }

      this.queuedTrigger = null;
      this.hidden = false;
      this.classList.add("active");
      this.setAttribute("aria-hidden", "false");
      document.body.classList.add("st-shipping-upsell-panel-open");
    }

    hide({ preserveQueue = false } = {}) {
      this.classList.remove("active");
      this.hidden = true;
      this.setAttribute("aria-hidden", "true");
      document.body.classList.remove("st-shipping-upsell-panel-open");

      if (!preserveQueue) {
        this.queuedTrigger = null;
      }
    }

    onPanelClose() {
      this.checkoutBypass = false;
      this.hide();
    }

    onCheckoutClick(event) {
      const checkoutButton = event.target.closest(
        '#offcanvas__mini_cart button[name="checkout"]'
      );
      if (!checkoutButton || this.checkoutBypass) {
        this.checkoutBypass = false;
        return;
      }

      if (!this.shouldUsePanel()) return;

      // First click opens the panel; second click proceeds to checkout.
      if (this.classList.contains("active")) {
        this.checkoutBypass = true;
        return;
      }

      if (this.isCookieBannerVisible()) {
        this.queuedTrigger = "before-checkout";
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.show("before-checkout");
    }

    onCartUpdate(event) {
      const cartData = event?.cartData;
      if (!cartData) return;

      const itemCount = cartData.item_count ?? 0;
      const total = cartData.total_price ?? 0;
      const wasEmpty = this.lastItemCount === 0;
      this.lastItemCount = itemCount;

      if (this.thresholdCents > 0 && total >= this.thresholdCents) {
        this.hide();
        return;
      }

      if (
        event?.source === "product-form" &&
        wasEmpty &&
        itemCount === 1 &&
        total < this.thresholdCents
      ) {
        this.show("first-add");
        return;
      }

      if (this.classList.contains("active") && !this.isUnderThreshold()) {
        this.hide();
      }
    }

    refreshFromSections(sections) {
      if (!sections?.["cart-shipping-upsell-panel"] || !this.contentRoot) return;

      const cartNotification = document.querySelector("cart-notification");
      const innerHtml = cartNotification?.getSectionInnerHTML?.(
        sections["cart-shipping-upsell-panel"]
      );

      if (innerHtml == null) return;

      this.contentRoot.innerHTML = innerHtml;
      this.bindPanelEvents();
      this.syncFromContent();

      if (
        !this.isUnderThreshold() ||
        !this.querySelector("[data-upsell-panel-content]")
      ) {
        this.hide();
      }
    }

    notifyDrawerOpened() {
      if (this.shouldUsePanel()) {
        this.show("drawer-open");
      }
    }

    notifyFirstAdd(cartData) {
      const itemCount = cartData?.item_count ?? 0;
      const total = cartData?.total_price ?? 0;

      if (itemCount === 1 && total < this.thresholdCents && !this.isMobileViewport()) {
        this.show("first-add");
      }
    }

    formatMoney(subunitCents, taxable = false) {
      const base = Number(subunitCents);
      if (!Number.isFinite(base)) return "";

      const amount = taxable ? Math.round(base * 1.2) : base;

      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(amount);
      }

      return `£${(amount / 100).toFixed(2)}`;
    }

    updatePreview(triggerEl, itemEl) {
      const item =
        itemEl ||
        triggerEl?.closest("[data-upsell-suggestion-item]") ||
        triggerEl?.closest("[data-upsell-panel-card]");
      const content = this.querySelector("[data-upsell-panel-content]");
      if (!content || !item || !triggerEl) return;

      const preview = content.querySelector("[data-upsell-preview]");
      const projectedEl = content.querySelector("[data-upsell-projected-total]");
      const unlockedEl = content.querySelector("[data-upsell-preview-unlocked]");

      if (!preview || !projectedEl) return;

      let addCents = 0;

      if (triggerEl.dataset?.priceCents) {
        addCents = parseInt(triggerEl.dataset.priceCents, 10) || 0;
      } else if (triggerEl.selectedOptions?.[0]?.dataset?.priceCents) {
        addCents =
          parseInt(triggerEl.selectedOptions[0].dataset.priceCents, 10) || 0;
      } else {
        const priceEl = item.querySelector("[data-cart-suggestion-price]");
        const priceText = priceEl?.textContent?.replace(/[^\d.]/g, "");
        if (priceText) {
          addCents = Math.round(parseFloat(priceText) * 100);
        }
      }

      const projected = this.cartTotalCents + addCents;
      projectedEl.textContent = this.formatMoney(projected, false);

      if (unlockedEl) {
        const unlocked = projected >= this.thresholdCents;
        unlockedEl.hidden = !unlocked;
      }

      preview.hidden = false;

      content
        .querySelectorAll("[data-upsell-suggestion-item], [data-upsell-panel-card]")
        .forEach((el) => el.classList.remove("is-previewing"));
      item.classList.add("is-previewing");
    }
  }

  customElements.define("cart-shipping-upsell-panel", CartShippingUpsellPanel);
}
